import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import OpportunityCard from "@/components/OpportunityCard";
import { GridSkeleton, EmptyState } from "@/components/Loaders";
import { useSaved } from "@/lib/useSaved";
import { Button } from "@/components/ui/button";
import { Search, SlidersHorizontal, GitCompare, Telescope, X } from "lucide-react";
import { toast } from "sonner";

const SORTS = [
  { key: "overall", label: "Top Score" },
  { key: "demand", label: "Most Demanded" },
  { key: "monetization", label: "Most Profitable" },
  { key: "trend_velocity", label: "Rising" },
  { key: "new", label: "Newest" },
];

export default function Discover() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [opps, setOpps] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const { savedIds, toggleSave } = useSaved();

  const category = params.get("category") || "all";
  const sort = params.get("sort") || "overall";
  const trending = params.get("trending");
  const isNew = params.get("is_new");

  const fetchOpps = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (category !== "all") q.set("category", category);
      q.set("sort", sort);
      if (trending) q.set("trending", "true");
      if (isNew) q.set("is_new", "true");
      if (params.get("search")) q.set("search", params.get("search"));
      const res = await api.get(`/opportunities?${q.toString()}`);
      setOpps(res.data);
    } finally { setLoading(false); }
  }, [category, sort, trending, isNew, params]);

  useEffect(() => { fetchOpps(); }, [fetchOpps]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v && v !== "all") p.set(k, v); else p.delete(k);
    if (k === "category" || k === "sort") { p.delete("trending"); p.delete("is_new"); }
    setParams(p);
  };

  const submitSearch = (e) => { e.preventDefault(); setParam("search", search); };

  const toggleCompare = (opp) => {
    setSelected((s) => {
      if (s.find((o) => o.slug === opp.slug)) return s.filter((o) => o.slug !== opp.slug);
      if (s.length >= 3) { toast("Compare up to 3 at a time"); return s; }
      return [...s, opp];
    });
  };

  const runCompare = () => {
    if (selected.length < 2) { toast("Select at least 2 opportunities"); return; }
    navigate(`/compare?slugs=${selected.map((o) => o.slug).join(",")}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50 flex items-center gap-3">
            <Telescope className="w-8 h-8 text-cyan-400" />
            {trending ? "Trending Opportunities" : isNew ? "New Opportunities" : "Opportunity Feed"}
          </h1>
          <p className="text-slate-400 mt-1">{opps.length} scored opportunities ranked by AI intelligence.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={compareMode ? "default" : "outline"} onClick={() => { setCompareMode(!compareMode); setSelected([]); }}
            className={compareMode ? "bg-cyan-500 text-slate-950" : "border-white/15 text-slate-300"} data-testid="compare-mode-btn">
            <GitCompare className="w-4 h-4 mr-1.5" />Compare
          </Button>
        </div>
      </div>

      <form onSubmit={submitSearch} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 mb-5 focus-within:border-cyan-500/50">
        <Search className="w-4 h-4 text-slate-500" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search opportunities, categories, tags…"
          className="bg-transparent text-sm text-slate-200 outline-none flex-1" data-testid="discover-search-input" />
        {params.get("search") && <button type="button" onClick={() => { setSearch(""); setParam("search", ""); }}><X className="w-4 h-4 text-slate-500" /></button>}
      </form>

      <div className="flex flex-wrap items-center gap-2 mb-8">
        <SlidersHorizontal className="w-4 h-4 text-slate-500 mr-1" />
        <Chip active={category === "all"} onClick={() => setParam("category", "all")}>All</Chip>
        {cats.map((c) => <Chip key={c} active={category === c} onClick={() => setParam("category", c)}>{c}</Chip>)}
        <div className="w-px h-5 bg-white/10 mx-2" />
        {SORTS.map((s) => <Chip key={s.key} active={sort === s.key} onClick={() => setParam("sort", s.key)} accent>{s.label}</Chip>)}
      </div>

      {loading ? <GridSkeleton /> : opps.length === 0 ? (
        <EmptyState icon={Telescope} title="No opportunities found" subtitle="Try adjusting your filters or search." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {opps.map((o) => (
            <OpportunityCard key={o.slug} opp={o} saved={savedIds.has(o.slug)} onSave={toggleSave}
              compareMode={compareMode} selected={!!selected.find((x) => x.slug === o.slug)} onToggleCompare={toggleCompare} />
          ))}
        </div>
      )}

      {compareMode && selected.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 glass rounded-full px-5 py-3 flex items-center gap-4 border border-cyan-500/30">
          <span className="text-sm text-slate-300">{selected.length} selected</span>
          <Button size="sm" onClick={runCompare} className="bg-cyan-500 text-slate-950 rounded-full" data-testid="run-compare-btn">Compare Now</Button>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children, accent }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${active ? (accent ? "border-indigo-500 bg-indigo-500/15 text-indigo-200" : "border-cyan-500 bg-cyan-500/15 text-cyan-200") : "border-white/10 text-slate-400 hover:border-white/25"}`}>
      {children}
    </button>
  );
}
